<template>
  <div class="flex w-full justify-start">
    <div class="flex w-full max-w-[92%] gap-3 sm:max-w-[80%]">
      <div
        class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
        <Icon icon="heroicons:bolt" class="size-5" />
      </div>
      <div
        class="min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-iris-200 bg-iris-50/80 px-5 py-4 shadow-sm dark:border-iris-800 dark:bg-iris-950/30">
        <p class="text-[15px] font-medium leading-relaxed text-charcoal-900 dark:text-charcoal-100">
          {{ config.title }}
        </p>
        <p v-if="config.description" class="mt-1 text-xs text-charcoal-500 dark:text-charcoal-400">{{
          config.description }}</p>

        <Stack full marginTop="medium" gap="medium">
          <div v-for="field in config.fields" :key="field.name" class="w-full">
            <InputText v-if="field.type === 'text'" v-model="values[field.name]" :label="field.label"
              :placeholder="field.placeholder" :disabled="disabled" size="small" class="w-full" />

            <Textarea v-else-if="field.type === 'textarea'" v-model="values[field.name]" :label="field.label"
              :placeholder="field.placeholder" :disabled="disabled" rows="3" size="small" class="w-full" />

            <Select v-else-if="field.type === 'select'" v-model="values[field.name]" :options="field.options"
              :optionLabel="null" :optionValue="null" :label="field.label" :placeholder="field.label" showClear
              :disabled="disabled" size="small" class="w-full" />

            <AutoComplete v-else-if="field.type === 'account'" v-model="values[field.name]"
              :apiSearch="searchAccountsByKeyword" optionLabel="name" :delay="500" showClear :disabled="disabled"
              placeholder="Search Accounts..." class="w-full" />
          </div>
        </Stack>

        <Box gap="small" marginTop="medium">
          <Button size="sm" color="purple" :disabled="disabled || !isValid" @click="submit">
            Submit
          </Button>
          <Button size="sm" :disabled="disabled" @click="cancel">
            Cancel
          </Button>
        </Box>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import { Icon } from '@iconify/vue'
import { uniqBy } from 'lodash'
import { v2 } from '@/services/api.js'
import { Box, Stack } from '@/components/Core/Layout'
import Button from '@/components/Core/Button.vue'
import InputText from '@/components/Core/Form/InputText.vue'
import Textarea from '@/components/Core/Form/Textarea.vue'
import Select from '@/components/Core/Form/Select.vue'
import AutoComplete from '@/components/Core/Form/AutoComplete.vue'

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['answer'])

function initialValues(config) {
  const values = {}
  for (const field of config.fields) {
    values[field.name] = field.default ?? (field.type === 'account' ? null : '')
  }
  return values
}

const values = reactive(initialValues(props.config))

watch(() => props.config, (config) => {
  Object.assign(values, initialValues(config))
})

const isValid = computed(() => {
  const check = props.config.isValid
  return typeof check === 'function' ? check(values) : true
})

async function searchAccountsByKeyword(options, query) {
  if (!query) return options

  const accounts = (await v2.get(`/accounts?filter[search]=${query}`)).data

  return uniqBy([...options, ...accounts], 'id')
}

function submit() {
  if (props.disabled || !isValid.value) return

  emit('answer', props.config.buildAnswer(values))
}

function cancel() {
  if (props.disabled) return

  emit('answer', `Cancelled the ${props.config.title.toLowerCase()} request.`)
}
</script>
